import React, { useContext, useState, useEffect } from "react";
import AdminNavBar from "@/components/AdminNavBar";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Trophy, ArrowLeft, Download, UserPlus, X, Plus, Minus, Eye, Edit, Trash2, Award, FileText, MessageSquare, BarChart3, ArrowUpDown, Mail, Send, ClipboardCheck, Save, RotateCcw, Shield } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatDateUK, getLocalTodayStr } from "@/utils/dateUtils";
import Logo from "@/components/Logo";
import AuditLogModal from "@/components/admin/AuditLogModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCompetitionView() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [competition, setCompetition] = useState(null);
  const [report, setReport] = useState(null);
  const [course, setCourse] = useState(null); // Linked course (for par/SI surfacing)
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showCreatePlayer, setShowCreatePlayer] = useState(false);
  const [showEnterScore, setShowEnterScore] = useState(false);
  const [showViewRounds, setShowViewRounds] = useState(false);
  const [showViewBestScores, setShowViewBestScores] = useState(false);
  const [showEditRound, setShowEditRound] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerRounds, setPlayerRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [playerEmail, setPlayerEmail] = useState("");
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerEmail, setNewPlayerEmail] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const [messagePriority, setMessagePriority] = useState("medium");
  const [messages, setMessages] = useState([]);
  const [showMessageAnalytics, setShowMessageAnalytics] = useState(false);
  const [selectedMessageAnalytics, setSelectedMessageAnalytics] = useState(null);
  const [showBulkEntry, setShowBulkEntry] = useState(false);
  const [bulkRounds, setBulkRounds] = useState([]);
  const [editingBulkRoundIndex, setEditingBulkRoundIndex] = useState(null);
  const [roundDate, setRoundDate] = useState(''); // Empty default - force user to select
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [courseHandicap, setCourseHandicap] = useState('');
  const [playerBestScores, setPlayerBestScores] = useState(null);
  const [showSendCertificatesDialog, setShowSendCertificatesDialog] = useState(false);
  const [sendingCertificates, setSendingCertificates] = useState(false);
  const [showVerifyHandicaps, setShowVerifyHandicaps] = useState(false);
  const [handicapData, setHandicapData] = useState([]);
  const [savingHandicaps, setSavingHandicaps] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  // When set, opens the audit modal pre-filtered to just this player's rows.
  const [auditForPlayer, setAuditForPlayer] = useState(null); // {id, name} | null
  // Removed adminInputValues state (was used for selective mode)
  // Helper function to get hole range based on competition format
  const getHoleRange = (holeFormat) => {
    if (holeFormat === "front_9") {
      return { start: 1, end: 9, total: 9 };
    } else if (holeFormat === "back_9") {
      return { start: 10, end: 18, total: 9 };
    } else {
      return { start: 1, end: 18, total: 18 };
    }
  };

  const [scores, setScores] = useState(
    Array.from({ length: 18 }, (_, i) => ({ hole: i + 1, strokes: 0 }))
  );

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard");
    } else if (user) {
      fetchData();
    }
  }, [user, navigate, id]);

  const fetchData = async () => {
    try {
      const [compResp, reportResp] = await Promise.all([
        axios.get(`${API}/admin/competitions`, { withCredentials: true }),
        axios.get(`${API}/admin/competitions/${id}/report`, { withCredentials: true })
      ]);
      
      const comp = compResp.data.find(c => c.id === id);
      setCompetition(comp);
      setReport(reportResp.data);

      // If the comp is linked to a course, pull course detail so par/SI/CR/SR can be surfaced
      if (comp?.course_id) {
        try {
          const courseResp = await axios.get(`${API}/courses/${comp.course_id}`, { withCredentials: true });
          setCourse(courseResp.data);
        } catch (e) {
          // Non-fatal: just skip the par strip if course fails to load
          setCourse(null);
        }
      } else {
        setCourse(null);
      }
    } catch (error) {
      toast.error("Failed to load competition data");
    } finally {
      setLoading(false);
    }
  };

  // Resolve the active tee + hole range for the current comp (for par/SI summaries)
  const getActiveTeeInfo = () => {
    if (!course) return null;
    const tees = course.tees || [];
    const tee = (competition?.tee_id && tees.find(t => t.id === competition.tee_id)) || tees[0];
    if (!tee) return null;
    const holeFormat = competition?.hole_format || "18";
    const [start, end] = holeFormat === "front_9"
      ? [1, 9]
      : holeFormat === "back_9"
        ? [10, 18]
        : [1, 18];
    const holes = (tee.holes || []).filter(h => h.hole >= start && h.hole <= end);
    const totalPar = holes.reduce((s, h) => s + (parseInt(h.par) || 0), 0);
    return { tee, holes, totalPar, start, end };
  };
  const formatVsPar = (score, par) => {
    if (!score || !par) return null;
    const diff = score - par;
    if (diff === 0) return "E";
    return diff > 0 ? `+${diff}` : `${diff}`;
  };

  const handleToggleRegistration = async () => {
    try {
      await axios.put(`${API}/admin/competitions/${id}`, {
        is_registration_open: !competition.is_registration_open
      });
      toast.success("Registration status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update registration");
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/competitions/${id}/players`, {
        email: playerEmail
      });
      toast.success("Player added successfully");
      setShowAddPlayer(false);
      setPlayerEmail("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add player");
    }
  };

  const handleRemovePlayer = async (playerId) => {
    if (!window.confirm("Are you sure you want to remove this player?")) return;
    
    try {
      await axios.delete(`${API}/admin/competitions/${id}/players/${playerId}`);
      toast.success("Player removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove player");
    }
  };

  const handleCreatePlayer = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/players/create`, {
        name: newPlayerName,
        email: newPlayerEmail
      });
      toast.success("Player created successfully");
      
      // Now add them to the competition
      await axios.post(`${API}/admin/competitions/${id}/players`, {
        email: newPlayerEmail
      });
      
      setShowCreatePlayer(false);
      setNewPlayerName("");
      setNewPlayerEmail("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create player");
    }
  };

  // Full round score change handler
  const handleScoreChange = (holeIndex, value) => {
    const newScores = [...scores];
    newScores[holeIndex].strokes = parseInt(value) || 0;
    setScores(newScores);
  };

  // Removed selective mode functions

  // Get the best score for a specific hole
  const getBestScoreForHole = (holeNum) => {
    if (!playerBestScores || !playerBestScores.hole_scores) return null;
    const holeScore = playerBestScores.hole_scores.find(hs => hs.hole === holeNum);
    return holeScore ? holeScore.strokes : null;
  };

  // Removed getNumImprovedScores function

  // Removed handleEntryModeToggle function

  const handleSubmitScore = async (e) => {
    e.preventDefault();
    
    if (!roundDate || courseHandicap === '') {
      toast.error("Please enter both round date and course handicap");
      return;
    }

    // Use scores for the competition's hole format
    const scoresToSubmit = scores;

    // Check for NEWLY ACHIEVED hole-in-one BEFORE submitting (not if they already had a hole-in-one)
    const newHolesInOne = scoresToSubmit.filter(s => {
      if (s.strokes !== 1) return false; // Not a hole-in-one
      // Check if this is a NEW hole-in-one (better than previous best)
      const bestScore = playerBestScores?.hole_scores?.find(h => h.hole === s.hole);
      return !bestScore || bestScore.strokes > 1; // New if no previous best OR previous best was > 1
    });
    
    if (newHolesInOne.length > 0) {
      const holeNumbers = newHolesInOne.map(h => h.hole).join(', ');
      const confirmed = window.confirm(
        `🎉 Congratulations! ${selectedPlayer.user_name} has a score of 1 on hole(s): ${holeNumbers}\n\n` +
        `Is this a HOLE IN ONE?\n\n` +
        `Click OK to confirm and post celebration message, or Cancel to review the scores.`
      );
      if (!confirmed) {
        return; // Admin wants to review scores
      }
    }

    // Check for NEWLY ACHIEVED score of 2 (if competition has this feature enabled)
    const newScoresOfTwo = competition?.recognize_score_of_2 ? scoresToSubmit.filter(s => {
      if (s.strokes !== 2) return false; // Not a score of 2
      // Check if this is a NEW score of 2 (better than previous best)
      const bestScore = playerBestScores?.hole_scores?.find(h => h.hole === s.hole);
      return !bestScore || bestScore.strokes > 2; // New if no previous best OR previous best was > 2
    }) : [];
    
    if (newScoresOfTwo.length > 0) {
      const holeNumbers = newScoresOfTwo.map(h => h.hole).join(', ');
      const confirmed = window.confirm(
        `💎 Excellent! ${selectedPlayer.user_name} has a score of 2 on hole(s): ${holeNumbers}\n\n` +
        `This is an EXCELLENT 2 achievement!\n\n` +
        `Click OK to confirm and post celebration, or Cancel to review the scores.`
      );
      if (!confirmed) {
        return; // Admin wants to review scores
      }
    }

    try {
      await axios.post(`${API}/admin/competitions/${id}/players/${selectedPlayer.player_id}/rounds`, {
        round_date: roundDate,
        course_handicap: parseInt(courseHandicap),
        scores: scoresToSubmit
      }, { withCredentials: true });
      
      // If there was a NEW hole-in-one, post celebration message
      if (newHolesInOne.length > 0) {
        try {
          const holeNumbers = newHolesInOne.map(h => h.hole);
          // Post celebration message on behalf of the player
          await axios.post(`${API}/competitions/${id}/celebrate-hole-in-one`, {
            holes: holeNumbers,
            date: roundDate,
            player_name: selectedPlayer.user_name
          }, { withCredentials: true });
          toast.success("🎉 Round submitted! Hole-in-one celebration posted!");
        } catch (msgError) {
          console.error("Failed to post celebration message:", msgError);
          toast.warning("Round submitted, but celebration message failed to post");
        }
      } else if (newScoresOfTwo.length > 0) {
        // If there was a NEW score of 2, post celebration message
        try {
          const holeNumbers = newScoresOfTwo.map(h => h.hole);
          await axios.post(`${API}/competitions/${id}/celebrate-score-of-2`, {
            holes: holeNumbers,
            date: roundDate,
            player_name: selectedPlayer.user_name
          }, { withCredentials: true });
          toast.success("💎 Round submitted! Excellent 2 celebration posted!");
        } catch (msgError) {
          console.error("Failed to post celebration message:", msgError);
          toast.warning("Round submitted, but celebration message failed to post");
        }
      } else {
        toast.success("Round scores submitted successfully");
      }
      
      setShowEnterScore(false);
      setSelectedPlayer(null);
      setRoundDate(''); // Reset to empty - force user to select
      setCourseHandicap('');
      const holeFormat = competition?.hole_format || "18";
      const { start, total } = getHoleRange(holeFormat);
      setScores(Array.from({ length: total }, (_, i) => ({ hole: start + i, strokes: 0 })));
      fetchData();
    } catch (error) {
      console.error("Score submission error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to submit scores");
    }
  };

  const openScoreEntry = async (player) => {
    setSelectedPlayer(player);
    setRoundDate(''); // Empty default - force user to select
    setCourseHandicap('');
    
    // Get hole range based on competition format
    const holeFormat = competition?.hole_format || "18";
    const { start, total } = getHoleRange(holeFormat);
    
    // Fetch player's best scores to display
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/players/${player.player_id}/best-scores`, {
        withCredentials: true
      });
      setPlayerBestScores(response.data);
      
      // Check if this is player's first round
      const hasBestScores = response.data && response.data.hole_scores && response.data.hole_scores.length > 0;
      
      if (hasBestScores) {
        // Subsequent rounds - pre-populate with best scores
        const initialScores = Array.from({ length: total }, (_, i) => {
          const holeNum = start + i;
          const bestHole = response.data.hole_scores.find(h => h.hole === holeNum);
          return {
            hole: holeNum,
            strokes: bestHole ? bestHole.strokes : 0
          };
        });
        setScores(initialScores);
      } else {
        // First round - start with zeros
        setScores(Array.from({ length: total }, (_, i) => ({ hole: start + i, strokes: 0 })));
      }
    } catch (error) {
      console.error("Failed to fetch best scores:", error);
      // Default to zeros if fetch fails
      setScores(Array.from({ length: total }, (_, i) => ({ hole: start + i, strokes: 0 })));
    }
    
    setShowEnterScore(true);
  };

  const openBulkEntry = async (player) => {
    setSelectedPlayer(player);
    setBulkRounds([]);
    setEditingBulkRoundIndex(null);
    
    // Fetch player's existing rounds to determine if first round or subsequent
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/players/${player.player_id}/rounds`, {
        withCredentials: true
      });
      setPlayerRounds(response.data);
      
      // Fetch best scores if they exist
      if (response.data.length > 0) {
        const bestResponse = await axios.get(`${API}/admin/competitions/${id}/players/${player.player_id}/best-scores`, {
          withCredentials: true
        });
        setPlayerBestScores(bestResponse.data);
      } else {
        setPlayerBestScores(null);
      }
    } catch (error) {
      setPlayerRounds([]);
      setPlayerBestScores(null);
    }
    
    setShowBulkEntry(true);
  };

  const viewLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/leaderboard`, {
        withCredentials: true
      });
      setLeaderboardData(response.data);
      setShowLeaderboard(true);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      toast.error("Failed to load leaderboard");
    }
  };

  const addRoundToBulk = () => {
    const isFirstRound = playerRounds.length === 0 && bulkRounds.length === 0;
    
    // Get previous round scores to pre-populate
    let previousScores = [];
    if (bulkRounds.length > 0) {
      // Get scores from the last bulk round
      previousScores = bulkRounds[bulkRounds.length - 1].scores;
    } else if (playerBestScores && playerBestScores.hole_scores) {
      // Get best scores from existing rounds
      previousScores = playerBestScores.hole_scores.map(hs => ({
        hole: hs.hole,
        strokes: hs.strokes
      }));
    }
    
    // For first round, default to 4 for all holes; otherwise use previous scores
    const holeFormat = competition?.hole_format || "18";
    const { start: hStart, total: hTotal } = getHoleRange(holeFormat);
    const defaultScores = isFirstRound 
      ? Array.from({ length: hTotal }, (_, i) => ({ hole: hStart + i, strokes: 4 }))
      : previousScores.length > 0 
        ? previousScores 
        : Array.from({ length: hTotal }, (_, i) => ({ hole: hStart + i, strokes: 4 }));
    
    // Get previous handicap to default
    let previousHandicap = '';
    if (bulkRounds.length > 0) {
      previousHandicap = bulkRounds[bulkRounds.length - 1].handicap;
    }
    
    const newRound = {
      date: '', // Empty default - force user to select date
      handicap: previousHandicap,
      scores: defaultScores,
      isFirstRound: isFirstRound,
      tempId: Date.now()
    };
    
    setBulkRounds([...bulkRounds, newRound]);
  };

  const updateBulkRound = (tempId, field, value) => {
    setBulkRounds(bulkRounds.map(round => 
      round.tempId === tempId ? { ...round, [field]: value } : round
    ));
  };

  const updateBulkRoundScore = (tempId, hole, strokes) => {
    setBulkRounds(bulkRounds.map(round => {
      if (round.tempId === tempId) {
        const existingScores = round.scores.filter(s => s.hole !== hole);
        if (strokes && strokes > 0) {
          return { ...round, scores: [...existingScores, { hole, strokes: parseInt(strokes) }].sort((a, b) => a.hole - b.hole) };
        }
        return { ...round, scores: existingScores };
      }
      return round;
    }));
  };

  const deleteBulkRound = (tempId) => {
    setBulkRounds(bulkRounds.filter(round => round.tempId !== tempId));
  };

  const submitBulkRounds = async () => {
    // Check for duplicate dates
    const dates = bulkRounds.map(r => r.date);
    const uniqueDates = new Set(dates);
    if (dates.length !== uniqueDates.size) {
      const duplicates = dates.filter((date, index) => dates.indexOf(date) !== index);
      toast.error(`Duplicate dates found: ${duplicates.join(', ')}. Each round must have a unique date.`);
      return;
    }
    
    // Check for dates that conflict with existing rounds
    const existingDates = playerRounds.map(r => r.round_date);
    const conflicts = dates.filter(date => existingDates.includes(date));
    if (conflicts.length > 0) {
      toast.error(`Date(s) already used in existing rounds: ${conflicts.join(', ')}. Please choose different dates.`);
      return;
    }
    
    // Validate all rounds
    for (let i = 0; i < bulkRounds.length; i++) {
      const round = bulkRounds[i];
      
      if (!round.date) {
        toast.error(`Round ${i + 1}: Please select a date`);
        return;
      }
      
      if (round.handicap === '' || parseInt(round.handicap) < 0 || parseInt(round.handicap) > 54) {
        toast.error(`Round ${i + 1}: Please enter a valid Course Handicap (0-54)`);
        return;
      }
      
      const holeFormat = competition?.hole_format || "18";
      const { total } = getHoleRange(holeFormat);
      
      if (round.scores.length !== total) {
        toast.error(`Round ${i + 1}: Must have scores for all ${total} holes`);
        return;
      }
      
      // Validate score values
      const invalidScores = round.scores.filter(s => s.strokes <= 0 || s.strokes > 10);
      if (invalidScores.length > 0) {
        toast.error(`Round ${i + 1}: All scores must be between 1 and 10`);
        return;
      }
    }
    
    // Sort rounds by date before submitting (chronological order)
    const sortedRounds = [...bulkRounds].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Submit rounds one by one in chronological order
    try {
      for (let i = 0; i < sortedRounds.length; i++) {
        const round = sortedRounds[i];
        await axios.post(
          `${API}/admin/competitions/${id}/players/${selectedPlayer.player_id}/rounds`,
          {
            round_date: round.date,
            course_handicap: parseInt(round.handicap),
            scores: round.scores
          },
          { withCredentials: true }
        );
      }
      
      toast.success(`Successfully submitted ${sortedRounds.length} round(s) in chronological order`);
      setShowBulkEntry(false);
      setBulkRounds([]);
      setSelectedPlayer(null);
      fetchData(); // Refresh main report
    } catch (error) {
      console.error("Bulk submit error:", error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to submit rounds";
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const openViewRounds = async (player) => {
    setSelectedPlayer(player);
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/players/${player.player_id}/rounds`, {
        withCredentials: true
      });
      setPlayerRounds(response.data);
      setShowViewRounds(true);
    } catch (error) {
      toast.error("Failed to load rounds");
    }
  };

  const openViewBestScores = async (player) => {
    setSelectedPlayer(player);
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/players/${player.player_id}/best-scores`, {
        withCredentials: true
      });
      setPlayerBestScores(response.data);
      setShowViewBestScores(true);
    } catch (error) {
      toast.error("Failed to load best scores");
    }
  };

  const openEditRound = (round) => {
    setSelectedRound(round);
    setRoundDate(round.round_date);
    setCourseHandicap(round.course_handicap || '');
    setScores(round.scores);
    setShowEditRound(true);
  };

  const handleUpdateRound = async (e) => {
    e.preventDefault();
    
    // Validate handicap
    if (courseHandicap === '' || parseInt(courseHandicap) < 0 || parseInt(courseHandicap) > 54) {
      toast.error("Please enter a valid Course Handicap (0-54)");
      return;
    }
    
    // Validate scores
    const invalidScores = scores.filter(s => s.strokes <= 0 || s.strokes > 20);
    if (invalidScores.length > 0) {
      toast.error("Please enter valid scores (1-10) for all holes");
      return;
    }

    try {
      await axios.put(
        `${API}/admin/competitions/${id}/players/${selectedPlayer.player_id}/rounds/${selectedRound.id}`,
        {
          round_date: roundDate,
          course_handicap: parseInt(courseHandicap),
          scores: scores
        },
        { withCredentials: true }
      );
      toast.success("Round updated successfully");
      setShowEditRound(false);
      
      // Refresh rounds list
      const response = await axios.get(`${API}/admin/competitions/${id}/players/${selectedPlayer.player_id}/rounds`, {
        withCredentials: true
      });
      setPlayerRounds(response.data);
      fetchData(); // Refresh main report
    } catch (error) {
      console.error("Update round error:", error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to update round";
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleDeleteRound = async (roundId) => {
    if (!window.confirm("Are you sure you want to delete this round? This will recalculate the player's best scores.")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/competitions/${id}/players/${selectedPlayer.player_id}/rounds/${roundId}`, {
        withCredentials: true
      });
      toast.success("Round deleted successfully");
      
      // Refresh rounds list
      const response = await axios.get(`${API}/admin/competitions/${id}/players/${selectedPlayer.player_id}/rounds`, {
        withCredentials: true
      });
      setPlayerRounds(response.data);
      fetchData(); // Refresh main report
    } catch (error) {
      toast.error("Failed to delete round");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/export`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${competition.name}_report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report exported successfully");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const handleGenerateCertificates = async () => {
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/certificates`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${competition.name}_Certificates_Top3.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Certificates generated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate certificates");
    }
  };

  const handleGenerateLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/competitions/${id}/leaderboard-pdf`, {
        responseType: "blob",
        withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${competition.name}_Final_Leaderboard.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Leaderboard generated successfully");
    } catch (error) {
      // Handle blob error response
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.detail || "Failed to generate leaderboard");
        } catch {
          toast.error("Failed to generate leaderboard");
        }
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate leaderboard");
      }
    }
  };

  const handleSendCertificates = async () => {
    setSendingCertificates(true);
    try {
      const response = await axios.post(
        `${API}/admin/competitions/${id}/send-certificates`,
        {},
        { withCredentials: true }
      );
      
      const { sent_count, total_recipients, failed_emails } = response.data;
      
      if (failed_emails && failed_emails.length > 0) {
        const failedNames = failed_emails.map(f => f.name).join(", ");
        toast.warning(`Certificates sent to ${sent_count}/${total_recipients} players. Failed: ${failedNames}`);
      } else {
        toast.success(`Successfully sent certificates to top ${sent_count} players!`);
      }
      
      setShowSendCertificatesDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send certificates");
    } finally {
      setSendingCertificates(false);
    }
  };

  const fetchFinalHandicaps = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/competitions/${id}/final-handicaps`,
        { withCredentials: true }
      );
      const players = response.data.players.map(p => ({
        ...p,
        editing_handicap: p.final_handicap !== null && p.final_handicap !== undefined
          ? p.final_handicap
          : p.last_round_handicap
      }));
      setHandicapData(players);
      setShowVerifyHandicaps(true);
    } catch (error) {
      toast.error("Failed to fetch handicap data");
    }
  };

  const handleSaveHandicaps = async () => {
    setSavingHandicaps(true);
    try {
      const handicaps = handicapData.map(p => ({
        player_id: p.player_id,
        final_handicap: Math.round(parseFloat(p.editing_handicap) || 0)
      }));
      await axios.put(
        `${API}/admin/competitions/${id}/final-handicaps`,
        { handicaps },
        { withCredentials: true }
      );
      toast.success("Final handicaps saved. Leaderboard will now use these values.");
      setShowVerifyHandicaps(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save handicaps");
    } finally {
      setSavingHandicaps(false);
    }
  };

  const handleClearHandicaps = async () => {
    if (!window.confirm("Clear all verified handicaps? The leaderboard will revert to using each player's last round handicap.")) return;
    try {
      await axios.delete(
        `${API}/admin/competitions/${id}/final-handicaps`,
        { withCredentials: true }
      );
      toast.success("Final handicaps cleared");
      setShowVerifyHandicaps(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to clear handicaps");
    }
  };

  const updateHandicapValue = (playerIdx, value) => {
    setHandicapData(prev => {
      const updated = [...prev];
      updated[playerIdx] = { ...updated[playerIdx], editing_handicap: value };
      return updated;
    });
  };

  const handleDeleteCompetition = async () => {
    const confirmText = `Are you sure you want to DELETE this competition?\n\n"${competition.name}"\n\nThis will permanently remove:\n- All player registrations\n- All round scores\n- All best scores\n\nThis action CANNOT be undone!`;
    
    if (!window.confirm(confirmText)) {
      return;
    }

    // Double confirmation for safety
    const finalConfirm = window.prompt(
      `Type "DELETE" to confirm deletion of "${competition.name}":`
    );

    if (finalConfirm !== "DELETE") {
      toast.error("Deletion cancelled - confirmation text did not match");
      return;
    }

    try {
      await axios.delete(`${API}/admin/competitions/${id}`);
      toast.success("Competition deleted successfully");
      navigate("/admin/competitions");
    } catch (error) {
      toast.error("Failed to delete competition");
    }
  };

  const handlePostMessage = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/messages`, {
        title: messageTitle,
        content: messageContent,
        message_type: "competition",
        priority: messagePriority,
        competition_id: id
      }, { withCredentials: true });
      
      toast.success("Message posted successfully");
      setShowMessageDialog(false);
      setMessageTitle("");
      setMessageContent("");
      setMessagePriority("medium");
      fetchMessages(); // Refresh messages list
    } catch (error) {
      toast.error("Failed to post message");
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/competitions/${id}/messages`, {
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`, {
        withCredentials: true
      });
      toast.success("Message deleted successfully");
      fetchMessages();
    } catch (error) {
      console.error("Delete message error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to delete message");
    }
  };

  const handleBulkDeleteMessages = async () => {
    if (!window.confirm("Are you sure you want to delete all competition messages?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/messages/bulk?competition_id=${id}`, {
        withCredentials: true
      });
      toast.success("All messages deleted successfully");
      fetchMessages();
    } catch (error) {
      console.error("Bulk delete error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to delete messages");
    }
  };

  const handleViewAnalytics = async (messageId) => {
    try {
      const response = await axios.get(`${API}/admin/messages/${messageId}/analytics`, {
        withCredentials: true
      });
      setSelectedMessageAnalytics(response.data);
      setShowMessageAnalytics(true);
    } catch (error) {
      toast.error("Failed to load analytics");
    }
  };

  useEffect(() => {
    if (user && competition) {
      fetchMessages();
    }
  }, [user, competition]);


  if (!user || loading || !competition) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-ink">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <AdminNavBar />
      {/* Header */}
      <header className="bg-white border-b border-line py-6 px-8 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Logo variant="icon" size={36} />
            <h1 className="text-xl font-semibold text-ink tracking-tight">Golf Eclectic</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/competitions")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-8 py-12">
        {/* Competition Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-semibold text-ink tracking-tight mb-4">{competition.name}</h2>
          <div className="flex items-center space-x-6 text-ink-muted">
            <span>{formatDateUK(competition.start_date)} - {formatDateUK(competition.end_date)}</span>
            <span>•</span>
            <span>{competition.total_rounds} Rounds</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center space-x-3 bg-white px-6 py-3 rounded-lg border border-line">
            <Label htmlFor="registration-toggle" className="text-sm font-medium">
              Registration
            </Label>
            <Switch
              id="registration-toggle"
              data-testid="registration-toggle"
              checked={competition.is_registration_open}
              onCheckedChange={handleToggleRegistration}
            />
            <span className="text-sm text-ink-muted">
              {competition.is_registration_open ? "Open" : "Closed"}
            </span>
          </div>
        </div>

        {/* Action Buttons - Organized by function */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Left: Player Management */}
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-ink-soft uppercase mb-1">Player Management</p>
              <Button
                data-testid="create-player-button"
                onClick={() => setShowCreatePlayer(true)}
                className="bg-ink hover:bg-blue-700 flex items-center justify-center space-x-2"
              >
                <UserPlus className="w-4 h-4" />
                <span>Create New Player</span>
              </Button>
              <Button
                data-testid="add-player-button"
                onClick={() => setShowAddPlayer(true)}
                className="bg-ink hover:bg-ink-hover flex items-center justify-center space-x-2"
              >
                <UserPlus className="w-4 h-4" />
                <span>Add Existing Player</span>
              </Button>
              <Button
                data-testid="email-players-button"
                onClick={() => navigate(`/admin/send-email?competition_id=${id}`)}
                className="bg-accent-deep hover:bg-accent-deep flex items-center justify-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>Email Players</span>
              </Button>
              <Button
                data-testid="post-message-button"
                onClick={() => setShowMessageDialog(true)}
                className="bg-ink hover:bg-ink-hover flex items-center justify-center space-x-2"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Post Message</span>
              </Button>
            </div>

            {/* Center: Reports & Leaderboard */}
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-ink-soft uppercase mb-1">Reports & Leaderboard</p>
              <Button
                data-testid="verify-handicaps-button"
                onClick={fetchFinalHandicaps}
                className="bg-orange-600 hover:bg-orange-700 flex items-center justify-center space-x-2"
              >
                <ClipboardCheck className="w-4 h-4" />
                <span>Verify Handicaps</span>
              </Button>
              <Button
                data-testid="view-leaderboard-button"
                onClick={viewLeaderboard}
                className="bg-ink hover:bg-ink-hover flex items-center justify-center space-x-2"
              >
                <BarChart3 className="w-4 h-4" />
                <span>View Leaderboard</span>
              </Button>
              <Button
                data-testid="export-button"
                onClick={handleExport}
                variant="outline"
                className="flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export to Excel</span>
              </Button>
            </div>

            {/* Right: Downloads & Management */}
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-ink-soft uppercase mb-1">Downloads & Management</p>
              <Button
                data-testid="certificates-button"
                onClick={handleGenerateCertificates}
                className="bg-yellow-600 hover:bg-yellow-700 flex items-center justify-center space-x-2"
              >
                <Award className="w-4 h-4" />
                <span>Top 3 Certificates</span>
              </Button>
              <Button
                data-testid="send-certificates-button"
                onClick={() => setShowSendCertificatesDialog(true)}
                className="bg-accent-deep hover:bg-accent-deep flex items-center justify-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>Email Top 3</span>
              </Button>
              <Button
                data-testid="leaderboard-button"
                onClick={handleGenerateLeaderboard}
                variant="outline"
                className="flex items-center justify-center space-x-2"
              >
                <FileText className="w-4 h-4" />
                <span>Leaderboard PDF</span>
              </Button>
              <Button
                data-testid="audit-log-button"
                onClick={() => setShowAuditLog(true)}
                variant="outline"
                className="flex items-center justify-center space-x-2 border-accent-deep text-ink hover:bg-accent-tint-2"
              >
                <Shield className="w-4 h-4" />
                <span>Audit Log</span>
              </Button>
              <Button
                onClick={async () => {
                  if (!window.confirm('Renumber all rounds in this competition by date order?')) return;
                  try {
                    await axios.post(`${API}/admin/utilities/renumber-all-rounds`, {}, { withCredentials: true });
                    toast.success("Rounds renumbered successfully");
                    fetchData(); // Refresh to show updated numbers
                  } catch (error) {
                    toast.error("Failed to renumber rounds");
                  }
                }}
                variant="outline"
                className="flex items-center justify-center space-x-2 border-accent text-ink hover:bg-accent-tint-2"
              >
                <ArrowUpDown className="w-4 h-4" />
                <span>Renumber Rounds</span>
              </Button>
              <Button
                data-testid="delete-competition-button"
                onClick={handleDeleteCompetition}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 flex items-center justify-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Competition</span>
              </Button>
            </div>
          </div>

        {/* Players Report */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Players & Scores</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Course / par summary strip — only when comp has a linked course */}
            {(() => {
              const teeInfo = getActiveTeeInfo();
              if (!teeInfo) return null;
              const { tee, totalPar } = teeInfo;
              return (
                <div
                  data-testid="admin-course-strip"
                  className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 rounded-lg bg-app border border-line text-sm"
                >
                  <Trophy className="w-4 h-4 text-accent flex-shrink-0" />
                  <span className="text-ink font-medium truncate">
                    {course?.club_name && course.club_name !== course?.name ? `${course.club_name} · ` : ""}{course?.name}
                  </span>
                  {tee?.name && (
                    <span className="text-ink-soft">·</span>
                  )}
                  {tee?.name && (
                    <span className="text-ink-muted">{tee.name}{tee.gender ? ` (${tee.gender})` : ""}</span>
                  )}
                  <span className="text-ink-soft">·</span>
                  <span className="font-mono tabular-nums text-ink-muted">
                    par <span className="text-ink font-semibold">{totalPar}</span>
                  </span>
                  {tee?.course_rating && (
                    <>
                      <span className="text-ink-soft">·</span>
                      <span className="font-mono tabular-nums text-ink-muted">CR {tee.course_rating}</span>
                    </>
                  )}
                  {tee?.slope_rating && (
                    <>
                      <span className="text-ink-soft">·</span>
                      <span className="font-mono tabular-nums text-ink-muted">SR {tee.slope_rating}</span>
                    </>
                  )}
                </div>
              );
            })()}
            {report && report.players.length === 0 ? (
              <div className="text-center py-8 text-ink-muted">
                No players registered yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Rank</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Player</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Rounds</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Gross</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">H/Cap</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Net Score</th>
                      <th className="text-right py-3 px-4 font-semibold text-ink-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report && report.players.map((player, index) => {
                      const teeInfo = getActiveTeeInfo();
                      const vsPar = teeInfo && player.gross_score > 0
                        ? formatVsPar(player.gross_score, teeInfo.totalPar)
                        : null;
                      return (
                      <tr key={player.player_id} className="border-b border-line hover:bg-app">
                        <td className="py-3 px-4">{index + 1}</td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-ink">{player.user_name}</p>
                            <p className="text-sm text-ink-muted">{player.user_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`font-semibold ${player.rounds_completed >= competition.total_rounds ? 'text-accent-deep' : 'text-ink-muted'}`}>
                            {player.rounds_completed} / {competition.total_rounds}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-semibold text-ink-muted">
                              {player.gross_score > 0 ? player.gross_score : "-"}
                            </span>
                            {vsPar && (
                              <span
                                data-testid={`admin-vspar-${player.player_id}`}
                                className={`font-mono tabular-nums text-[11px] ${
                                  vsPar.startsWith("-") ? "text-accent font-semibold"
                                  : vsPar === "E" ? "text-ink"
                                  : "text-ink-muted"
                                }`}
                                title="Strokes vs par"
                              >
                                ({vsPar})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-ink-muted">
                            {player.course_handicap || 0}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-bold text-ink text-lg">
                            {player.net_score > 0 ? player.net_score.toFixed(1) : "-"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              data-testid={`view-rounds-${player.player_id}`}
                              size="sm"
                              onClick={() => openViewRounds(player)}
                              variant="outline"
                              className="flex items-center space-x-1"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Rounds</span>
                            </Button>
                            {/* Per-player audit shortcut. Opens the same
                                audit modal but pre-filtered to this player
                                — a two-click investigation ("did they
                                double-submit or edit?") instead of loading
                                the whole comp log and filtering by hand. */}
                            <Button
                              data-testid={`player-activity-${player.player_id}`}
                              size="sm"
                              onClick={() => setAuditForPlayer({ id: player.player_id, name: player.name })}
                              variant="outline"
                              className="flex items-center space-x-1"
                              title="Recent activity for this player"
                            >
                              <Shield className="w-4 h-4" />
                              <span>Activity</span>
                            </Button>
                            <Button
                              data-testid={`view-best-${player.player_id}`}
                              size="sm"
                              onClick={() => openViewBestScores(player)}
                              variant="outline"
                              className="flex items-center space-x-1 bg-warm-tint/40 hover:bg-warm-tint/60 text-warm"
                              title="View player's best scores"
                            >
                              <Award className="w-4 h-4" />
                              <span>Best Scores</span>
                            </Button>
                            <Button
                              data-testid={`enter-score-${player.player_id}`}
                              size="sm"
                              onClick={() => openScoreEntry(player)}
                              disabled={player.rounds_completed >= competition.total_rounds}
                              className="bg-ink hover:bg-ink-hover flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={player.rounds_completed >= competition.total_rounds ? `Player has completed all ${competition.total_rounds} rounds` : 'Enter a new round'}
                            >
                              <Plus className="w-4 h-4" />
                              <span>Enter Score</span>
                            </Button>
                            <Button
                              data-testid={`bulk-entry-${player.player_id}`}
                              size="sm"
                              onClick={() => openBulkEntry(player)}
                              variant="outline"
                              className="bg-app hover:bg-accent-tint text-accent-deep flex items-center space-x-1"
                              title="Enter multiple rounds at once"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Bulk Entry</span>
                            </Button>
                            <Button
                              data-testid={`remove-player-${player.player_id}`}
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemovePlayer(player.player_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages Management Section */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl">Competition Messages</CardTitle>
              <Button
                onClick={handleBulkDeleteMessages}
                variant="outline"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                disabled={messages.length === 0}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-8 text-ink-muted">
                No messages posted yet
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className="border rounded-lg p-4 hover:bg-app transition"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-lg">{msg.title}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          msg.priority === 'high' 
                            ? 'bg-red-100 text-red-800'
                            : msg.priority === 'medium'
                            ? 'bg-warm-tint/60 text-warm'
                            : 'bg-accent-tint text-accent-deep'
                        }`}>
                          {msg.priority?.toUpperCase() || 'MEDIUM'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleViewAnalytics(msg.id)}
                          size="sm"
                          variant="outline"
                          className="text-accent-deep"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Analytics
                        </Button>
                        <Button
                          onClick={() => handleDeleteMessage(msg.id)}
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-ink-muted mb-2 whitespace-pre-wrap">{msg.content}</p>
                    <div className="text-sm text-ink-soft">
                      Posted: {msg.created_at 
                        ? formatDateUK(new Date(msg.created_at).toISOString().split('T')[0])
                        : 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </main>

      {/* Add Player Dialog */}
      <Dialog open={showAddPlayer} onOpenChange={setShowAddPlayer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Existing Player to Competition</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPlayer} className="space-y-4">
            <div>
              <Label htmlFor="player-email">Player Email</Label>
              <Input
                id="player-email"
                data-testid="player-email-input"
                type="email"
                value={playerEmail}
                onChange={(e) => setPlayerEmail(e.target.value)}
                required
                placeholder="player@example.com"
              />
              <p className="text-sm text-ink-muted mt-1">Email of player already registered in system</p>
            </div>
            <Button
              data-testid="submit-add-player"
              type="submit"
              className="w-full bg-ink hover:bg-ink-hover"
            >
              Add Player
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Player Dialog */}
      <Dialog open={showCreatePlayer} onOpenChange={setShowCreatePlayer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Player</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePlayer} className="space-y-4">
            <div>
              <Label htmlFor="new-player-name">Full Name</Label>
              <Input
                id="new-player-name"
                data-testid="new-player-name"
                type="text"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                required
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="new-player-email">Email</Label>
              <Input
                id="new-player-email"
                data-testid="new-player-email"
                type="email"
                value={newPlayerEmail}
                onChange={(e) => setNewPlayerEmail(e.target.value)}
                required
                placeholder="player@example.com"
              />
              <p className="text-sm text-ink-muted mt-1">For players without Google accounts</p>
            </div>
            <Button
              data-testid="submit-create-player"
              type="submit"
              className="w-full bg-ink hover:bg-blue-700"
            >
              Create and Add to Competition
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enter Score Dialog */}
      <Dialog open={showEnterScore} onOpenChange={setShowEnterScore}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Enter Round Score for {selectedPlayer?.user_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitScore} className="space-y-4">
            {/* Entry mode toggle removed - always use full round entry */}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="score-round-date">Round Date</Label>
                <Input
                  id="score-round-date"
                  data-testid="score-round-date"
                  type="date"
                  value={roundDate}
                  onChange={(e) => setRoundDate(e.target.value)}
                  required
                  min={competition?.start_date}
                  max={competition?.end_date && new Date(competition.end_date) < new Date() 
                    ? competition.end_date 
                    : getLocalTodayStr()}
                  className="max-w-xs"
                />
              </div>
              <div>
                <Label htmlFor="admin-course-handicap">Course Handicap</Label>
                <Input
                  id="admin-course-handicap"
                  data-testid="admin-course-handicap"
                  type="number"
                  min="0"
                  max="54"
                  value={courseHandicap}
                  onChange={(e) => setCourseHandicap(e.target.value)}
                  required
                  placeholder="e.g., 12"
                  className="max-w-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-3 block">
                Hole Scores (1-10 strokes per hole)
                {playerBestScores && (
                  <span className="text-sm text-ink-muted font-normal ml-2">
                    (Current best scores pre-filled)
                  </span>
                )}
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {scores.map((score, index) => {
                  const bestScore = playerBestScores?.hole_scores?.find(hs => hs.hole === score.hole);
                  const currentBest = bestScore?.strokes || 0;
                  const isImprovement = currentBest > 0 && score.strokes > 0 && score.strokes < currentBest;
                  const isWorse = currentBest > 0 && score.strokes > currentBest;
                  
                  return (
                    <div key={score.hole} className={`hole-card p-3 rounded-lg border-2 ${
                      isImprovement ? 'border-accent/30 bg-accent-tint-2' : 
                      isWorse ? 'border-red-300 bg-red-50' : 
                      'border-line bg-white'
                    }`}>
                      <Label className="text-sm font-semibold text-ink-muted mb-2 block text-center">
                        Hole {score.hole}
                      </Label>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          onClick={() => {
                            const newScores = [...scores];
                            newScores[index] = { ...score, strokes: Math.max(0, score.strokes - 1) };
                            setScores(newScores);
                          }}
                          disabled={score.strokes === 0}
                          className="h-10 w-10 p-0 bg-surface text-ink border border-line hover:bg-app hover:border-line-strong disabled:opacity-30"
                          size="sm"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <div className={`flex items-center justify-center w-12 h-10 text-lg font-bold rounded border-2 ${
                          score.strokes === 0 ? 'bg-surface-2 text-ink-soft border-line-strong' : 
                          score.strokes === 1 && currentBest === 1 ? 'bg-warm-tint/60 text-warm border-warm/40' : 
                          score.strokes === 2 && currentBest === 2 && competition?.recognize_score_of_2 ? 'bg-accent-tint text-accent-deep border-accent/30' : 
                          'bg-white text-ink border-line-strong'
                        }`}>
                          {score.strokes}
                        </div>
                        <Button
                          type="button"
                          onClick={() => {
                            const newScores = [...scores];
                            newScores[index] = { ...score, strokes: Math.min(10, score.strokes + 1) };
                            setScores(newScores);
                          }}
                          disabled={score.strokes === 10}
                          className="h-10 w-10 p-0 bg-ink hover:bg-ink-hover disabled:opacity-30"
                          size="sm"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {score.strokes === 0 && (
                        <div className="text-xs text-red-600 mt-1 text-center font-medium">
                          ⚠️ Enter score
                        </div>
                      )}
                      {currentBest > 0 && (
                        <div className="text-xs text-ink-muted mt-1 text-center">
                          Best: {currentBest}
                          {currentBest === 1 && score.strokes === 1 && (
                            <span className="block text-warm font-bold mt-0.5">🏆 Hole in One!</span>
                          )}
                          {currentBest === 2 && score.strokes === 2 && competition?.recognize_score_of_2 && (
                            <span className="block text-accent-deep font-bold mt-0.5">💎 Excellent 2!</span>
                          )}
                        </div>
                      )}
                      {isImprovement && (
                        <div className="text-xs text-accent-deep mt-1 text-center font-medium">
                          ✓ Improved!
                        </div>
                      )}
                      {isWorse && score.strokes > 0 && (
                        <div className="text-xs text-red-700 mt-1 text-center font-medium">
                          ⚠️ Worse than best
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-app p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-ink-muted">Round Total</span>
                <span className="text-xl font-semibold text-ink tracking-tight">
                  {scores.reduce((sum, s) => sum + s.strokes, 0)}
                </span>
              </div>
            </div>

            <Button
              data-testid="submit-admin-scores"
              type="submit"
              className="w-full bg-ink hover:bg-ink-hover py-3"
            >
              Submit Round ({(() => { const hf = competition?.hole_format || "18"; return hf === "18" ? "18 holes" : "9 holes"; })()})
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Rounds Dialog */}
      <Dialog open={showViewRounds} onOpenChange={setShowViewRounds}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              All Rounds for {selectedPlayer?.user_name}
            </DialogTitle>
          </DialogHeader>
          {/* Explanation for admins: rounds are numbered by chronological
              play-date, not by when they were entered into the app. So if
              a round is back-dated (e.g. an admin correction), the numbering
              is re-computed automatically. Fixes confusion where "Round 1"
              might not be the first row entered. */}
          {playerRounds.length > 1 && (
            <div className="mb-3 text-[12px] text-ink-muted bg-app border border-line rounded-md px-3 py-2">
              <span className="font-medium text-ink">Note:</span> Rounds are numbered by play-date, not by entry order.
              If a round is back-dated (or an admin adds a round for an earlier date), all rounds are automatically renumbered
              so Round 1 is always the earliest chronologically.
            </div>
          )}
          {playerRounds.length === 0 ? (
            <div className="text-center py-8 text-ink-muted">
              No rounds recorded yet
            </div>
          ) : (
            <div className="space-y-4">
              {playerRounds.map((round) => (
                <Card key={round.id} className="border-2">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-lg">Round {round.round_number}</CardTitle>
                        <p className="text-sm text-ink-muted mt-1">{formatDateUK(round.round_date)}</p>
                        <p className="text-sm font-semibold text-accent-deep mt-1">
                          Course Handicap: {round.course_handicap ?? 'Not recorded'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-right mr-4">
                          <p className="text-sm text-ink-muted">Total</p>
                          <p className="text-xl font-semibold text-ink tracking-tight">
                            {round.scores.reduce((sum, s) => sum + s.strokes, 0)}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => openEditRound(round)}
                          variant="outline"
                          className="flex items-center space-x-1"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRound(round.id);
                          }}
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-6 md:grid-cols-9 gap-2">
                      {round.scores.map(score => {
                        const isHoleInOne = score.strokes === 1;
                        const isScoreOfTwo = score.strokes === 2 && competition?.recognize_score_of_2;
                        return (
                          <div key={score.hole} className={`text-center rounded p-2 ${
                            isHoleInOne ? 'bg-warm-tint/40 border-2 border-warm/40' : 
                            isScoreOfTwo ? 'bg-accent-tint-2 border-2 border-accent/30' : 
                            'bg-app'
                          }`}>
                            <div className="text-xs text-ink-soft font-medium">H{score.hole}</div>
                            <div className={`text-lg font-semibold ${
                              isHoleInOne ? 'text-warm' : 
                              isScoreOfTwo ? 'text-accent-deep' : 
                              'text-ink'
                            }`}>{score.strokes}</div>
                            {isHoleInOne && (
                              <div className="text-xs text-warm font-bold mt-0.5">🏆</div>
                            )}
                            {isScoreOfTwo && (
                              <div className="text-xs text-accent-deep font-bold mt-0.5">💎</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Best Scores Dialog */}
      <Dialog open={showViewBestScores} onOpenChange={setShowViewBestScores}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Award className="w-6 h-6 mr-2 text-warm" />
              Best Scores for {selectedPlayer?.user_name}
            </DialogTitle>
          </DialogHeader>
          {!playerBestScores || !playerBestScores.hole_scores || playerBestScores.hole_scores.length === 0 ? (
            <div className="text-center py-8 text-ink-muted">
              No scores recorded yet
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {(() => { const hf = competition?.hole_format || "18"; const { start, end } = getHoleRange(hf); return Array.from({ length: end - start + 1 }, (_, i) => start + i); })().map(hole => {
                  const holeData = playerBestScores.hole_scores.find(hs => hs.hole === hole);
                  const isHoleInOne = holeData && holeData.strokes === 1;
                  const isScoreOfTwo = holeData && holeData.strokes === 2 && competition?.recognize_score_of_2;
                  return (
                    <div key={hole} className={`p-3 rounded-lg border-2 text-center ${
                      isHoleInOne ? 'bg-warm-tint/40 border-warm/40' : 
                      isScoreOfTwo ? 'bg-accent-tint-2 border-accent/30' : 
                      'bg-white border-line'
                    }`}>
                      <div className="text-sm font-semibold text-ink-muted mb-2">Hole {hole}</div>
                      {holeData ? (
                        <>
                          <div className={`text-3xl font-bold ${
                            isHoleInOne ? 'text-warm' : 
                            isScoreOfTwo ? 'text-accent-deep' : 
                            'text-ink'
                          }`}>
                            {holeData.strokes}
                          </div>
                          <div className="text-xs text-ink-soft mt-1">{formatDateUK(holeData.round_date)}</div>
                          {isHoleInOne && (
                            <div className="text-xs text-warm font-bold mt-1">
                              🏆 Hole in One!
                            </div>
                          )}
                          {isScoreOfTwo && (
                            <div className="text-xs text-accent-deep font-bold mt-1">
                              💎 Excellent 2!
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-xl font-semibold text-ink tracking-tight-soft">-</div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="bg-app p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-ink-muted">Gross Total</p>
                    <p className="text-xl font-semibold text-ink tracking-tight">
                      {playerBestScores.hole_scores.reduce((sum, hs) => sum + hs.strokes, 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-ink-muted">Net Score</p>
                    <p className="text-xl font-semibold text-ink tracking-tight">
                      {selectedPlayer?.net_score > 0 ? selectedPlayer.net_score.toFixed(1) : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Round Dialog */}
      <Dialog open={showEditRound} onOpenChange={setShowEditRound}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Round {selectedRound?.round_number} for {selectedPlayer?.user_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRound} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-round-date">Round Date</Label>
                <Input
                  id="edit-round-date"
                  data-testid="edit-round-date"
                  type="date"
                  value={roundDate}
                  onChange={(e) => setRoundDate(e.target.value)}
                  required
                  min={competition?.start_date}
                  max={competition?.end_date && new Date(competition.end_date) < new Date() 
                    ? competition.end_date 
                    : getLocalTodayStr()}
                  className="max-w-xs"
                />
              </div>
              <div>
                <Label htmlFor="edit-course-handicap">Course Handicap</Label>
                <Input
                  id="edit-course-handicap"
                  data-testid="edit-course-handicap"
                  type="number"
                  min="0"
                  max="54"
                  value={courseHandicap}
                  onChange={(e) => setCourseHandicap(e.target.value)}
                  required
                  placeholder="e.g., 12"
                  className="max-w-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-3 block">Hole Scores (1-10 strokes per hole)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {scores.map((score, index) => {
                  // Find best score for this hole
                  const bestScore = playerBestScores?.hole_scores?.find(h => h.hole === score.hole);
                  const currentBest = bestScore?.strokes || 0;
                  
                  return (
                    <div key={score.hole} className="hole-card p-3 rounded-lg border border-line bg-white">
                      <Label className="text-sm font-semibold text-ink-muted mb-2 block text-center">
                        Hole {score.hole}
                      </Label>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          onClick={() => {
                            const newScores = [...scores];
                            newScores[index] = { ...score, strokes: Math.max(0, score.strokes - 1) };
                            setScores(newScores);
                          }}
                          disabled={score.strokes === 0}
                          className="h-10 w-10 p-0 bg-surface text-ink border border-line hover:bg-app hover:border-line-strong disabled:opacity-30"
                          size="sm"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <div className={`flex items-center justify-center w-12 h-10 text-lg font-bold rounded border-2 ${
                          score.strokes === 0 ? 'bg-surface-2 text-ink-soft border-line-strong' : 
                          score.strokes === 1 && currentBest === 1 ? 'bg-warm-tint/60 text-warm border-warm/40' : 
                          'bg-white text-ink border-line-strong'
                        }`}>
                          {score.strokes}
                        </div>
                        <Button
                          type="button"
                          onClick={() => {
                            const newScores = [...scores];
                            newScores[index] = { ...score, strokes: Math.min(10, score.strokes + 1) };
                            setScores(newScores);
                          }}
                          disabled={score.strokes === 10}
                          className="h-10 w-10 p-0 bg-ink hover:bg-ink-hover disabled:opacity-30"
                          size="sm"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {currentBest > 0 && (
                        <div className="text-xs text-ink-soft mt-1 text-center">
                          Best: {currentBest}
                          {currentBest === 1 && score.strokes === 1 && (
                            <span className="block text-warm font-bold mt-0.5">🏆 Hole in One!</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-app p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-ink-muted">Round Total</span>
                <span className="text-xl font-semibold text-ink tracking-tight">
                  {scores.reduce((sum, s) => sum + s.strokes, 0)}
                </span>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                data-testid="update-round-button"
                type="submit"
                className="flex-1 bg-ink hover:bg-ink-hover py-3"
              >
                Update Round
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditRound(false)}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Post Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Post Message to Competition</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePostMessage} className="space-y-4">
            <div>
              <Label htmlFor="message-title">Message Title</Label>
              <Input
                id="message-title"
                data-testid="message-title"
                value={messageTitle}
                onChange={(e) => setMessageTitle(e.target.value)}
                required
                placeholder="e.g., Important Update"
              />
            </div>
            <div>
              <Label htmlFor="message-priority">Priority</Label>
              <select
                id="message-priority"
                data-testid="message-priority"
                value={messagePriority}
                onChange={(e) => setMessagePriority(e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="low">Low - General Information</option>
                <option value="medium">Medium - Standard Update</option>
                <option value="high">High - Urgent/Important</option>
              </select>
            </div>
            <div>
              <Label htmlFor="message-content">Message</Label>
              <textarea
                id="message-content"
                data-testid="message-content"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                required
                placeholder="Enter your message here..."
                className="w-full min-h-[150px] p-3 border rounded-md"
              />
            </div>
            <Button
              data-testid="submit-message"
              type="submit"
              className="w-full bg-ink hover:bg-blue-700"
            >
              Post Message
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message Analytics Dialog */}
      <Dialog open={showMessageAnalytics} onOpenChange={setShowMessageAnalytics}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Message Analytics</DialogTitle>
          </DialogHeader>
          {selectedMessageAnalytics && (
            <div className="space-y-4">
              <div className="bg-app p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">{selectedMessageAnalytics.title}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-ink-muted">Priority:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                      selectedMessageAnalytics.priority === 'high' 
                        ? 'bg-red-100 text-red-800'
                        : selectedMessageAnalytics.priority === 'medium'
                        ? 'bg-warm-tint/60 text-warm'
                        : 'bg-accent-tint text-accent-deep'
                    }`}>
                      {selectedMessageAnalytics.priority.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="text-ink-muted">Type:</span>
                    <span className="ml-2 font-medium">{selectedMessageAnalytics.message_type}</span>
                  </div>
                  <div>
                    <span className="text-ink-muted">Posted:</span>
                    <span className="ml-2 font-medium">
                      {formatDateUK(new Date(selectedMessageAnalytics.created_at).toISOString().split('T')[0])}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-app p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-accent-deep">
                    {selectedMessageAnalytics.total_recipients}
                  </div>
                  <div className="text-sm text-ink-muted">Total Recipients</div>
                </div>
                <div className="bg-accent-tint-2 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-accent">
                    {selectedMessageAnalytics.read_count}
                  </div>
                  <div className="text-sm text-ink-muted">Read Count</div>
                </div>
                <div className="bg-accent-tint-2 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-accent">
                    {selectedMessageAnalytics.read_percentage}%
                  </div>
                  <div className="text-sm text-ink-muted">Read Rate</div>
                </div>
              </div>

              {selectedMessageAnalytics.read_by && selectedMessageAnalytics.read_by.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Read By:</h4>
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    {selectedMessageAnalytics.read_by.map((reader, idx) => (
                      <div 
                        key={idx} 
                        className="p-3 border-b last:border-b-0 hover:bg-app"
                      >
                        <div className="font-medium">{reader.name}</div>
                        <div className="text-sm text-ink-muted">{reader.email}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Entry Dialog */}
      <Dialog open={showBulkEntry} onOpenChange={setShowBulkEntry}>
        <DialogContent className="sm:max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Bulk Score Entry for {selectedPlayer?.user_name}
            </DialogTitle>
            <div className="mt-3 space-y-2">
              <p className="text-sm text-ink-muted">
                Enter multiple rounds in a spreadsheet-style table. Scores auto-populate from previous best. Only change improved holes.
              </p>
              <div className="bg-app border border-line rounded p-3 text-sm">
                <p className="font-semibold text-accent-deep mb-1">Important Rules:</p>
                <ul className="list-disc ml-4 text-accent-deep space-y-1">
                  <li>Each round must have a <strong>unique date</strong> (no duplicates)</li>
                  <li>Dates entered out of order will be <strong>automatically sorted chronologically</strong> before submission</li>
                  <li>Scores worse than current best shown in <span className="bg-red-50 text-red-700 font-bold px-1">red</span> (will be ignored by Eclectic rules)</li>
                  <li>Best scores shown in gray below each hole for reference</li>
                </ul>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Table view */}
            {bulkRounds.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-surface-2 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold border-r">Round</th>
                      <th className="px-2 py-2 text-left font-semibold border-r">Date</th>
                      <th className="px-2 py-2 text-left font-semibold border-r">H/Cap</th>
                      {(() => { const hf = competition?.hole_format || "18"; const { start, end } = getHoleRange(hf); return Array.from({ length: end - start + 1 }, (_, i) => start + i); })().map(hole => (
                        <th key={hole} className="px-1 py-2 text-center font-semibold border-r w-16">
                          {hole}
                        </th>
                      ))}
                      <th className="px-2 py-2 text-center font-semibold border-r bg-accent-tint-2">Gross</th>
                      <th className="px-2 py-2 text-center font-semibold border-r bg-app">Net</th>
                      <th className="px-2 py-2 text-center font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRounds.map((round, roundIndex) => {
                      const gross = round.scores.reduce((sum, s) => sum + (s.strokes || 0), 0);
                      const net = round.handicap ? (gross - (parseInt(round.handicap) / 2)).toFixed(1) : gross;
                      
                      // Check for duplicate dates
                      const allDates = bulkRounds.map(r => r.date);
                      const isDuplicateDate = allDates.filter(d => d === round.date).length > 1;
                      const isExistingDate = playerRounds.some(r => r.round_date === round.date);
                      
                      return (
                        <tr key={round.tempId} className="border-b hover:bg-app">
                          <td className="px-2 py-2 border-r font-medium">
                            {playerRounds.length + roundIndex + 1}
                          </td>
                          <td className="px-2 py-2 border-r">
                            <Input
                              type="date"
                              value={round.date}
                              onChange={(e) => updateBulkRound(round.tempId, 'date', e.target.value)}
                              min={competition?.start_date}
                              max={competition?.end_date}
                              className={`w-36 h-8 text-xs ${(isDuplicateDate || isExistingDate) ? 'border-red-500 bg-red-50' : ''}`}
                              title={isDuplicateDate ? 'Duplicate date!' : isExistingDate ? 'Date already used in existing round!' : ''}
                            />
                            {(isDuplicateDate || isExistingDate) && (
                              <p className="text-xs text-red-600 mt-1">
                                {isDuplicateDate ? 'Duplicate!' : 'Already used!'}
                              </p>
                            )}
                          </td>
                          <td className="px-2 py-2 border-r">
                            <Input
                              type="number"
                              min="0"
                              max="54"
                              value={round.handicap}
                              onChange={(e) => updateBulkRound(round.tempId, 'handicap', e.target.value)}
                              className="w-16 h-8 text-xs text-center"
                            />
                          </td>
                          {(() => { const hf = competition?.hole_format || "18"; const { start, end } = getHoleRange(hf); return Array.from({ length: end - start + 1 }, (_, i) => start + i); })().map(hole => {
                            const score = round.scores.find(s => s.hole === hole);
                            const currentValue = score?.strokes || '';
                            
                            // Get best score for this hole (from previous rounds or player's existing best)
                            let bestForHole = null;
                            if (roundIndex > 0) {
                              // Check previous bulk rounds
                              const prevBestScores = bulkRounds.slice(0, roundIndex).map(r => 
                                r.scores.find(s => s.hole === hole)?.strokes
                              ).filter(s => s);
                              if (prevBestScores.length > 0) {
                                bestForHole = Math.min(...prevBestScores);
                              }
                            }
                            if (bestForHole === null && playerBestScores?.hole_scores) {
                              const existing = playerBestScores.hole_scores.find(hs => hs.hole === hole);
                              if (existing) bestForHole = existing.strokes;
                            }
                            
                            // Check if current score is worse than best
                            const isWorseThanBest = bestForHole && currentValue > bestForHole;
                            
                            return (
                              <td key={hole} className="px-1 py-1 border-r">
                                <div className="flex flex-col items-center">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newVal = (currentValue || 0) + 1;
                                      if (newVal <= 10) updateBulkRoundScore(round.tempId, hole, newVal);
                                    }}
                                    className="w-full h-4 text-xs bg-surface-2 hover:bg-line rounded-t border"
                                  >
                                    ▲
                                  </button>
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={currentValue}
                                    onChange={(e) => updateBulkRoundScore(round.tempId, hole, e.target.value)}
                                    className={`w-full h-6 text-xs text-center border-x ${isWorseThanBest ? 'bg-red-50 text-red-700 font-bold' : ''}`}
                                    title={isWorseThanBest ? `Warning: ${currentValue} is worse than best (${bestForHole})` : ''}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newVal = (currentValue || 0) - 1;
                                      if (newVal >= 1) updateBulkRoundScore(round.tempId, hole, newVal);
                                    }}
                                    className="w-full h-4 text-xs bg-surface-2 hover:bg-line rounded-b border"
                                  >
                                    ▼
                                  </button>
                                  {bestForHole && (
                                    <span className="text-xs text-ink-soft mt-0.5">({bestForHole})</span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-2 py-2 border-r text-center font-bold bg-accent-tint-2">
                            {gross || '-'}
                          </td>
                          <td className="px-2 py-2 border-r text-center font-bold bg-app">
                            {round.handicap ? net : '-'}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteBulkRound(round.tempId)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button
                onClick={addRoundToBulk}
                variant="outline"
                disabled={bulkRounds.length >= (competition?.total_rounds - playerRounds.length)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Round {playerRounds.length + bulkRounds.length + 1}
                {bulkRounds.length >= (competition?.total_rounds - playerRounds.length) && 
                  <span className="ml-2 text-sm">(Max reached)</span>
                }
              </Button>
              
              {bulkRounds.length > 0 && (
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBulkEntry(false);
                      setBulkRounds([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitBulkRounds}
                    className="bg-ink hover:bg-ink-hover"
                  >
                    Submit All {bulkRounds.length} Round{bulkRounds.length > 1 ? 's' : ''}
                  </Button>
                </div>
              )}
            </div>
            
            {bulkRounds.length === 0 && (
              <div className="text-center py-12 text-ink-soft border-2 border-dashed rounded-lg">
                <FileText className="w-12 h-12 mx-auto mb-3 text-ink-soft" />
                <p className="text-lg font-medium mb-2">No rounds added yet</p>
                <p className="text-sm mb-4">Click "Add Round 1" to start entering scores</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Leaderboard Dialog */}
      <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center">
              <BarChart3 className="w-6 h-6 mr-2 text-accent" />
              Leaderboard - {competition?.name}
            </DialogTitle>
            <p className="text-sm text-ink-muted mt-2">
              Rankings based on Net Scores (Gross - Handicap ÷ 2)
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            {leaderboardData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-2">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold">Rank</th>
                      <th className="text-left py-3 px-4 font-semibold">Player</th>
                      <th className="text-center py-3 px-4 font-semibold">Gross</th>
                      <th className="text-center py-3 px-4 font-semibold">H/Cap</th>
                      <th className="text-center py-3 px-4 font-semibold">Net Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboardData.map((entry, index) => (
                      <tr 
                        key={entry.player_id}
                        className={`border-b hover:bg-app ${
                          index === 0 ? 'bg-warm-tint/40' : 
                          index === 1 ? 'bg-surface-2' : 
                          index === 2 ? 'bg-warm-tint/40' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <span className="font-bold text-lg">
                            {index === 0 && '🥇 '}
                            {index === 1 && '🥈 '}
                            {index === 2 && '🥉 '}
                            {entry.rank}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-semibold">{entry.user_name}</td>
                        <td className="py-3 px-4 text-center text-ink-muted">
                          {entry.gross_total}
                        </td>
                        <td className="py-3 px-4 text-center text-ink-muted text-sm">
                          {entry.course_handicap}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-bold text-ink text-lg">
                            {entry.net_total.toFixed(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-ink-soft">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-line-strong" />
                <p>No players with completed scores yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Certificates Confirmation Dialog */}
      <Dialog open={showSendCertificatesDialog} onOpenChange={setShowSendCertificatesDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center">
              <Send className="w-5 h-5 mr-2 text-accent-deep" />
              Send Top 3 Certificates via Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-app border border-line rounded-lg p-4">
              <p className="text-sm text-ink-muted mb-2">
                This will:
              </p>
              <ul className="text-sm text-ink-muted space-y-1 ml-4 list-disc">
                <li>Generate individual PDF certificates for the top 3 players</li>
                <li>Send personalized emails with certificates attached</li>
                <li>Use the current leaderboard rankings (based on net scores)</li>
              </ul>
            </div>

            <div className="bg-warm-tint/40 border border-warm/40 rounded-lg p-3">
              <p className="text-xs text-warm">
                <strong>Note:</strong> Make sure the leaderboard is final before sending certificates. This action will send emails immediately.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowSendCertificatesDialog(false)}
                variant="outline"
                className="flex-1"
                disabled={sendingCertificates}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendCertificates}
                className="flex-1 bg-accent-deep hover:bg-accent-deep"
                disabled={sendingCertificates}
              >
                {sendingCertificates ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Certificates
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Verify Final Handicaps Dialog */}
      <Dialog open={showVerifyHandicaps} onOpenChange={setShowVerifyHandicaps}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-warm" />
              Verify Final Handicaps
            </DialogTitle>
            <p className="text-sm text-ink-soft">Set the verified handicap for each player before finalising results.</p>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-app border border-line rounded-lg p-3">
              <p className="text-sm text-ink-muted">
                Set the <strong>verified playing handicap</strong> for each player. This handicap will be used for all leaderboard, PDF, and certificate calculations instead of the handicap from their last round.
              </p>
            </div>

            {handicapData.length === 0 ? (
              <p className="text-ink-soft text-center py-8">No active players in this competition.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-app">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-ink-muted">Player</th>
                      <th className="text-center px-3 py-3 font-semibold text-ink-muted">Rounds</th>
                      <th className="text-center px-3 py-3 font-semibold text-ink-muted">Gross</th>
                      <th className="text-center px-3 py-3 font-semibold text-ink-muted">Last Rd HCP</th>
                      <th className="text-center px-3 py-3 font-semibold text-ink-muted">
                        Final HCP
                      </th>
                      <th className="text-center px-3 py-3 font-semibold text-ink-muted">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {handicapData.map((player, idx) => {
                      const hcp = parseInt(player.editing_handicap) || 0;
                      const netScore = player.gross_score > 0
                        ? (player.gross_score - hcp / 2).toFixed(1)
                        : "-";
                      const changed = hcp !== player.last_round_handicap;
                      return (
                        <tr
                          key={player.player_id}
                          className={`border-t ${changed ? "bg-warm-tint/40" : ""}`}
                          data-testid={`handicap-row-${idx}`}
                        >
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-ink">{player.user_name}</div>
                            <div className="text-xs text-ink-soft">{player.user_email}</div>
                          </td>
                          <td className="text-center px-3 py-2.5 text-ink-muted">
                            {player.rounds_completed}
                          </td>
                          <td className="text-center px-3 py-2.5 font-medium">
                            {player.gross_score || "-"}
                          </td>
                          <td className="text-center px-3 py-2.5 text-ink-soft">
                            {player.last_round_handicap}
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <input
                              data-testid={`handicap-input-${idx}`}
                              type="number"
                              step="1"
                              min="0"
                              max="54"
                              value={player.editing_handicap}
                              onChange={(e) => updateHandicapValue(idx, e.target.value)}
                              className={`w-20 text-center border rounded px-2 py-1 text-sm font-semibold ${
                                changed
                                  ? "border-warm/40 bg-warm-tint/40 text-warm"
                                  : "border-line-strong"
                              }`}
                            />
                          </td>
                          <td className="text-center px-3 py-2.5 font-semibold text-ink">
                            {netScore}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {handicapData.length > 0 && (
              <div className="flex gap-3 pt-2">
                <Button
                  data-testid="clear-handicaps-button"
                  onClick={handleClearHandicaps}
                  variant="outline"
                  className="flex items-center gap-2 text-ink-muted"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset All
                </Button>
                <div className="flex-1" />
                <Button
                  onClick={() => setShowVerifyHandicaps(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="save-handicaps-button"
                  onClick={handleSaveHandicaps}
                  className="bg-orange-600 hover:bg-orange-700 flex items-center gap-2"
                  disabled={savingHandicaps}
                >
                  {savingHandicaps ? (
                    <>
                      <span className="animate-spin">...</span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save & Apply to Leaderboard
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Log Modal — opens either from the top-level Audit Log button
          (whole comp) or from a per-player Activity button (pre-filtered). */}
      <AuditLogModal
        open={showAuditLog || !!auditForPlayer}
        onClose={() => { setShowAuditLog(false); setAuditForPlayer(null); }}
        competitionId={id}
        competitionName={competition?.name}
        competitionEndDate={competition?.end_date}
        initialPlayerId={auditForPlayer?.id || undefined}
        initialPlayerName={auditForPlayer?.name || undefined}
      />
    </div>
  );
}
